import { isTextField } from "../../ui/input/text-field.ts";
import { reservedReason } from "./reserved.ts";

/**
 * Whether a keyboard is in use on this page (P1.9-T50; the author's ruling of 2026-10-07: "hidden from mobile, unless
 * a bluetooth/keyboard is plugged in."). A browser gives no dependable sign that a keyboard is attached, so the page
 * takes the first key press that can only have come from one and keeps it for the session: what a touch page hides
 * for want of a keyboard (Settings' Controls) shows from then on. It is the sign the design's touch mode asks the
 * client for (`tokens/touch.css`: key hints show only while a physical keyboard is in use).
 */

/**
 * A key press as this reads it: the subset of `KeyboardEvent` that tells a hardware key from an on-screen one.
 */
export interface SeenKey {
  readonly key: string;
  readonly isTrusted?: boolean;
  readonly isComposing?: boolean;
  readonly keyCode?: number;
  readonly target?: unknown;
}

/**
 * The key code browsers give a press an input method is still composing.
 */
const COMPOSING_CODE = 229;

/**
 * Whether a key press came from a hardware keyboard. An on-screen keyboard shows only while a text field has focus
 * and reports its presses unnamed or as composition, so a press counts when the browser made it (not a script), it
 * names its key, nothing is composing and no text field has focus. A phone's own buttons (volume, media, back) and a
 * key with no name are the reserved list's `system` and `unnamed` keys and never count.
 *
 * @param event The press.
 * @param textField Whether a target is a text field.
 * @returns True for a hardware key press.
 * @example
 * hardwareKey({ key: "w", isTrusted: true, target: document.body }, isTextField);
 * // true
 */
export function hardwareKey(event: SeenKey, textField: (target: unknown) => boolean): boolean {
  const reason = event.key === "" ? "unnamed" : reservedReason(event.key);
  return reason !== "system" && reason !== "unnamed" && event.isTrusted === true && event.isComposing !== true && event.keyCode !== COMPOSING_CODE && event.key !== "Unidentified" && event.key !== "Process" && !textField(event.target ?? null);
}

/**
 * Whether a keyboard has been used on the page, and who wants to hear when it first is.
 *
 * @example
 * const use = new KeyboardUse(isTextField);
 * window.addEventListener("keydown", (event) => use.note(event), true);
 */
export class KeyboardUse {
  private used = false;
  private readonly listeners = new Set<() => void>();

  /**
   * Starts with no keyboard seen.
   *
   * @param textField Whether a target is a text field.
   */
  public constructor(private readonly textField: (target: unknown) => boolean) {}

  /**
   * Whether a hardware key press has been seen this session.
   *
   * @returns True once one has.
   */
  public get seen(): boolean {
    return this.used;
  }

  /**
   * Whether Settings shows Controls: always on a page that is not a touch page, and on a touch page once a keyboard
   * is in use.
   *
   * @param touch Whether the page is a touch page (no hover, a coarse pointer).
   * @returns True when Controls shows.
   */
  public controlsShown(touch: boolean): boolean {
    return !touch || this.used;
  }

  /**
   * Takes a key press: the first from a hardware keyboard marks one in use and tells every listener.
   *
   * @param event The press.
   */
  public note(event: SeenKey): void {
    if (this.used || !hardwareKey(event, this.textField)) {
      return;
    }
    this.used = true;
    for (const listener of [...this.listeners]) {
      listener();
    }
  }

  /**
   * Reads whether a keyboard is in use. One function for the object's life, for a React store.
   *
   * @returns True once a hardware key press was seen.
   */
  public readonly getSnapshot = (): boolean => this.used;

  /**
   * Listens for the first hardware key press. One function for the object's life, for a React store.
   *
   * @param listener Called once, when a keyboard is first in use.
   * @returns A function that stops listening.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
}

/**
 * This page's keyboard use, fed every key press before anything else on the page sees it.
 */
export const pageKeyboard = new KeyboardUse(isTextField);

if (typeof window !== "undefined") {
  window.addEventListener("keydown", (event) => pageKeyboard.note(event), true);
}
